import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Product, ProductLot, UoM, Part, PurchaseOrder, WorkOrder, Serial,
  SearchProductsRequest, SearchProductsResponse,
  CreateProductRequest
} from '../../services/productApi';

export interface ProductState {
  // Product State
  products: Product[];
  selectedProduct: Product | null;
  productLoading: boolean;
  productError: string | null;
  productValidationErrors: string[];
  
  // Product Lot State
  productLots: ProductLot[];
  selectedProductLot: ProductLot | null;
  
  // Pagination and Filtering
  productPagination: {
    currentPage: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
  };
  
  // Search and Filters
  productSearchTerm: string;
  productActiveFilter: boolean | null;
  
  // Dropdown Data
  dropdownUoMs: UoM[];
  dropdownParts: Part[];
  dropdownPurchaseOrders: PurchaseOrder[];
  dropdownWorkOrders: WorkOrder[];
  dropdownSerials: Serial[];
  dropdownsLoading: boolean;
  
  // UI State
  productDialogOpen: boolean;
  productLotDialogOpen: boolean;
  
  // Export State
  exportLoading: boolean;
  exportError: string | null;
}

const initialState: ProductState = {
  // Product State
  products: [],
  selectedProduct: null,
  productLoading: false,
  productError: null,
  productValidationErrors: [],
  
  // Product Lot State
  productLots: [],
  selectedProductLot: null,
  
  // Pagination and Filtering
  productPagination: {
    currentPage: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 0,
  },
  
  // Search and Filters
  productSearchTerm: '',
  productActiveFilter: null,
  
  // Dropdown Data
  dropdownUoMs: [],
  dropdownParts: [],
  dropdownPurchaseOrders: [],
  dropdownWorkOrders: [],
  dropdownSerials: [],
  dropdownsLoading: false,
  
  // UI State
  productDialogOpen: false,
  productLotDialogOpen: false,
  
  // Export State
  exportLoading: false,
  exportError: null,
};

const productSlice = createSlice({
  name: 'product',
  initialState,
  reducers: {
    // Product Actions
    fetchProductsStart: (state, action: PayloadAction<SearchProductsRequest | undefined>) => {
      state.productLoading = true;
      state.productError = null;
    },

    fetchProductsSuccess: (state, action: PayloadAction<SearchProductsResponse>) => {
      state.productLoading = false;
      state.products = action.payload.data;
      state.productPagination = {
        currentPage: action.payload.page,
        pageSize: action.payload.limit,
        totalCount: action.payload.totalCount,
        totalPages: action.payload.totalPages,
      };
    },

    fetchProductsFailure: (state, action: PayloadAction<string>) => {
      state.productLoading = false;
      state.productError = action.payload;
    },

    fetchProductByIdStart: (state, action: PayloadAction<string>) => {
      state.productLoading = true;
      state.productError = null;
    },

    fetchProductByIdSuccess: (state, action: PayloadAction<Product>) => {
      state.productLoading = false;
      state.selectedProduct = action.payload;
    },

    fetchProductByIdFailure: (state, action: PayloadAction<string>) => {
      state.productLoading = false;
      state.productError = action.payload;
    },

    createProductStart: (state, action: PayloadAction<CreateProductRequest>) => {
      state.productLoading = true;
      state.productError = null;
      state.productValidationErrors = [];
    },

    createProductSuccess: (state, action: PayloadAction<Product>) => {
      state.productLoading = false;
      state.products.unshift(action.payload);
      state.selectedProduct = action.payload;
      state.productDialogOpen = false;
    },

    createProductFailure: (state, action: PayloadAction<string>) => {
      state.productLoading = false;
      state.productError = action.payload;
    },

    updateProductStart: (state, action: PayloadAction<Partial<Product>>) => {
      state.productLoading = true;
      state.productError = null;
      state.productValidationErrors = [];
    },

    updateProductSuccess: (state, action: PayloadAction<Product>) => {
      state.productLoading = false;
      const index = state.products.findIndex(p => p.id === action.payload.id);
      if (index !== -1) {
        state.products[index] = action.payload;
      }
      state.selectedProduct = action.payload;
    },

    updateProductFailure: (state, action: PayloadAction<string>) => {
      state.productLoading = false;
      state.productError = action.payload;
    },

    deleteProductStart: (state, action: PayloadAction<string>) => {
      state.productLoading = true;
      state.productError = null;
    },

    deleteProductSuccess: (state, action: PayloadAction<string>) => {
      state.productLoading = false;
      state.products = state.products.filter(p => p.id !== action.payload);
    },

    deleteProductFailure: (state, action: PayloadAction<string>) => {
      state.productLoading = false;
      state.productError = action.payload;
    },

    // Dropdown Actions
    fetchDropdownsStart: (state) => {
      state.dropdownsLoading = true;
    },

    fetchDropdownsSuccess: (state, action: PayloadAction<{
      uoms?: UoM[];
      parts?: Part[];
      purchaseOrders?: PurchaseOrder[];
      workOrders?: WorkOrder[];
      serials?: Serial[];
    }>) => {
      state.dropdownsLoading = false;
      if (action.payload.uoms) state.dropdownUoMs = action.payload.uoms;
      if (action.payload.parts) state.dropdownParts = action.payload.parts;
      if (action.payload.purchaseOrders) state.dropdownPurchaseOrders = action.payload.purchaseOrders;
      if (action.payload.workOrders) state.dropdownWorkOrders = action.payload.workOrders;
      if (action.payload.serials) state.dropdownSerials = action.payload.serials;
    },

    fetchDropdownsFailure: (state, action: PayloadAction<string>) => {
      state.dropdownsLoading = false;
      state.productError = action.payload;
    },

    // Search and Filter Actions
    setProductSearchTerm: (state, action: PayloadAction<string>) => {
      state.productSearchTerm = action.payload;
      state.productPagination.currentPage = 1;
    },

    setProductActiveFilter: (state, action: PayloadAction<boolean | null>) => {
      state.productActiveFilter = action.payload;
      state.productPagination.currentPage = 1;
    },

    setProductPagination: (state, action: PayloadAction<Partial<ProductState['productPagination']>>) => {
      state.productPagination = { ...state.productPagination, ...action.payload };
    },

    // UI Actions
    setSelectedProduct: (state, action: PayloadAction<Product | null>) => {
      state.selectedProduct = action.payload;
    },

    setProductDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.productDialogOpen = action.payload;
    },

    setProductLotDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.productLotDialogOpen = action.payload;
    },

    // Validation Actions
    setProductValidationErrors: (state, action: PayloadAction<string[]>) => {
      state.productValidationErrors = action.payload;
    },

    clearProductError: (state) => {
      state.productError = null;
      state.productValidationErrors = [];
    },

    // Export Actions
    exportProductStart: (state) => {
      state.exportLoading = true;
      state.exportError = null;
    },

    exportProductSuccess: (state) => {
      state.exportLoading = false;
    },

    exportProductFailure: (state, action: PayloadAction<string>) => {
      state.exportLoading = false;
      state.exportError = action.payload;
    },
  },
});

export const {
  // Product Actions
  fetchProductsStart, fetchProductsSuccess, fetchProductsFailure,
  fetchProductByIdStart, fetchProductByIdSuccess, fetchProductByIdFailure,
  createProductStart, createProductSuccess, createProductFailure,
  updateProductStart, updateProductSuccess, updateProductFailure,
  deleteProductStart, deleteProductSuccess, deleteProductFailure,
  
  // Dropdown Actions
  fetchDropdownsStart, fetchDropdownsSuccess, fetchDropdownsFailure,
  
  // Search and Filter Actions
  setProductSearchTerm, setProductActiveFilter, setProductPagination,
  
  // UI Actions
  setSelectedProduct, setProductDialogOpen, setProductLotDialogOpen,
  
  // Validation Actions
  setProductValidationErrors, clearProductError,
  
  // Export Actions
  exportProductStart, exportProductSuccess, exportProductFailure,
} = productSlice.actions;

export default productSlice.reducer; 